import { createContext, Dispatch, useCallback, useEffect, useReducer } from "react";
import { SessionStatus, useSessionBootstrap } from "../hooks/useSessionBootstrap";
import { onRemoteLogout } from "../utils/authBroadcast";
import { setCompanyId, setCompanyName } from "../utils/cacheStorage";
import { endSession } from "../utils/session";

interface AuthState {
  user: any;
  userCompany: string | null;
  newNotification: any;
  company?: any;
}

const initialState: AuthState = {
  user: null,
  userCompany: null,
  newNotification: false,
  company: null
};

interface AuthContextProps extends AuthState {
  status: SessionStatus;
  retrySession: () => void;
  dispatch: Dispatch<AuthAction>;
  logout: () => void;
  setCompany: (companyName: string, companyId: string) => void;
}

type AuthAction =
  | { type: "LOGIN"; payload: any }
  | { type: "LOGOUT"; payload?: any }
  | { type: "SET_COMPANY"; payload: string }
  | { type: "SET_COMPANY_ID"; payload: string }
  | { type: "NEW_NOTIFICATION"; payload?: boolean };

export const AuthContext = createContext<AuthContextProps>({
  ...initialState,
  status: "loading",
  retrySession: () => {},
  dispatch: () => {},
  logout: () => {},
  setCompany: () => {}
});

export const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "LOGIN":
      return { ...state, user: action.payload };
    case "LOGOUT":
      return {
        ...initialState
      };
    case "SET_COMPANY":
      setCompanyName(action.payload);
      return { ...state, userCompany: action.payload };
    case "SET_COMPANY_ID":
      setCompanyId(action.payload);
      return { ...state, userCompany: action.payload };
    case "NEW_NOTIFICATION":
      return { ...state, newNotification: Math.random() };
    default:
      return state;
  }
};

export const AuthContextProvider = ({ children }: any) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const { status, restoreSession } = useSessionBootstrap();

  const logout = useCallback(() => {
    endSession({ broadcast: true });
    dispatch({ type: "LOGOUT" });
  }, []);

  // Signing out in another tab signs this tab out too.
  useEffect(
    () =>
      onRemoteLogout(() => {
        endSession({ redirect: true });
        dispatch({ type: "LOGOUT" });
      }),
    []
  );

  const setCompany = useCallback((companyName: string, companyId: string) => {
    setCompanyName(companyName);
    dispatch({ type: "SET_COMPANY", payload: companyName });
    dispatch({ type: "SET_COMPANY_ID", payload: companyId });
  }, []);

  return (
    <AuthContext.Provider
      value={{ ...state, status, retrySession: restoreSession, dispatch, logout, setCompany }}
    >
      {children}
    </AuthContext.Provider>
  );
};
